import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiAlertTriangle, FiMail, FiX } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { Modal } from '../../components/UI/Modal';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { clientesService } from '../../services';

// Formatea una fecha 'YYYY-MM-DD' como local (evita el desfase de un día por UTC)
const formatFecha = (dateStr) => {
  if (!dateStr) return 'N/A';
  const parts = String(dateStr).split('T')[0].split('-');
  if (parts.length === 3) {
    return new Date(+parts[0], +parts[1] - 1, +parts[2]).toLocaleDateString('es-CO');
  }
  return new Date(dateStr).toLocaleDateString('es-CO');
};

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const ClienteEstadoCuenta = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [estadoCuenta, setEstadoCuenta] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Por defecto: últimos 90 días (no fechas fijas de 2024)
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return toISO(d);
  });
  const [dateTo, setDateTo] = useState(() => toISO(new Date()));

  // Estado para envío del estado de cuenta por correo
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    destinatario: '',
    copias: '',
    asunto: '',
    mensaje: ''
  });
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  useEffect(() => {
    loadFacturas();
  }, [id]);

  const loadFacturas = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Obtener estado de cuenta desde la API
      const result = await clientesService.getEstadoCuenta(id, {
        date_from: dateFrom,
        date_to: dateTo
      });

      if (result.success) {
        setEstadoCuenta(result.data);
      } else {
        setError(result.message || 'Error al cargar el estado de cuenta');
      }
    } catch (error) {
      console.error('Error cargando estado de cuenta:', error);
      setError('Error de conexión al cargar el estado de cuenta');
    } finally {
      setIsLoading(false);
    }
  };

  const openEmailModal = () => {
    setEmailData({
      destinatario: '',
      copias: '',
      asunto: `Estado de Cuenta - ${estadoCuenta?.client_name || 'Cliente'} - Betel Natural`,
      mensaje: ''
    });
    setShowEmailModal(true);
  };

  const processSendEmail = async () => {
    setIsSendingEmail(true);
    try {
      const payload = {
        destinatario: emailData.destinatario.trim() || null,
        copias: emailData.copias.trim() || null,
        asunto: emailData.asunto.trim() || null,
        mensaje: emailData.mensaje.trim() || null,
        date_from: dateFrom,
        date_to: dateTo
      };

      const result = await clientesService.sendEstadoCuentaEmail(id, payload);

      if (result.success) {
        alert(result.data?.message || 'Estado de cuenta enviado por correo');
        setShowEmailModal(false);
      } else {
        alert(result.message || 'No se pudo enviar el correo. Verifique que el cliente tenga email registrado.');
      }
    } catch (error) {
      console.error('Error enviando estado de cuenta por correo:', error);
      alert('Error al enviar el correo. Por favor intente nuevamente.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={3} message="Cargando estado de cuenta..." />;
  }

  return (
    <div className="cliente-estado-cuenta-page">
      {/* Header */}
      <div className="page-header-simple">
        <button onClick={() => navigate(`/system/clientes/${id}`)} className="btn btn--ghost">
          <FiArrowLeft /> Volver
        </button>
        <h1 className="page-title">ESTADO DE CUENTA</h1>
      </div>

      {/* Filtro de período */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Desde</label>
          <input
            type="date"
            className="form-input"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Hasta</label>
          <input
            type="date"
            className="form-input"
            value={dateTo}
            min={dateFrom}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>
        <button onClick={loadFacturas} className="btn btn--primary" style={{ height: '40px' }}>
          Aplicar
        </button>
        <button
          onClick={openEmailModal}
          className="btn btn--outline"
          style={{ height: '40px', marginLeft: 'auto' }}
          disabled={!estadoCuenta}
          title="Enviar el estado de cuenta por correo al cliente"
        >
          <FiMail /> Enviar por correo
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {!estadoCuenta ? (
        <div className="empty-state">
          <p>No se pudo cargar el estado de cuenta</p>
        </div>
      ) : (
        <>
          {/* Información del Cliente */}
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.1em' }}>{estadoCuenta.client_name}</h3>
            <div style={{ fontSize: '0.9em', color: '#6b7280' }}>
              Período: {formatFecha(estadoCuenta.period?.from)} - {formatFecha(estadoCuenta.period?.to)}
            </div>
          </div>

          {/* Alertas */}
          {estadoCuenta.summary?.overdue_balance > 0 && (
            <div className="alert alert--warning">
              <FiAlertTriangle />
              <div>
                <strong>Saldo Vencido:</strong> ${estadoCuenta.summary.overdue_balance?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
              </div>
            </div>
          )}

          {/* Resumen */}
          <div className="estado-cuenta-summary">
            <div className="summary-card">
              <div className="summary-label">Total Facturado</div>
              <div className="summary-value">${estadoCuenta.summary?.total_invoiced?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Total Pagado</div>
              <div className="summary-value">${estadoCuenta.summary?.total_paid?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>

          <div className="estado-cuenta-summary" style={{ marginTop: '10px' }}>
            <div className="summary-card warning">
              <div className="summary-label">Saldo Actual</div>
              <div className="summary-value">${estadoCuenta.summary?.current_balance?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
            </div>
            <div className="summary-card">
              <div className="summary-label">Saldo del Período</div>
              <div className="summary-value">${estadoCuenta.summary?.period_balance?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
            </div>
          </div>

          {/* Información de Crédito */}
          {estadoCuenta.credit_info && (
            <div style={{ marginTop: '20px' }}>
              <h3 className="section-title">Información de Crédito</h3>
              <div className="estado-cuenta-summary">
                <div className="summary-card">
                  <div className="summary-label">Límite de Crédito</div>
                  <div className="summary-value">${estadoCuenta.credit_info.credit_limit?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}</div>
                </div>
                <div className="summary-card">
                  <div className="summary-label">Crédito Disponible</div>
                  <div className="summary-value" style={{ color: estadoCuenta.credit_info.credit_available > 0 ? '#10b981' : '#ef4444' }}>
                    ${estadoCuenta.credit_info.credit_available?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div className="summary-card">
                  <div className="summary-label">Términos de Pago</div>
                  <div className="summary-value">{estadoCuenta.credit_info.payment_terms} días</div>
                </div>
              </div>
            </div>
          )}

          {/* Lista de Transacciones */}
          <div style={{ marginTop: '20px' }}>
            <h3 className="section-title">Transacciones del Período</h3>
            {!estadoCuenta.transactions || estadoCuenta.transactions.length === 0 ? (
              <div className="empty-state">
                <p>No hay transacciones en este período</p>
              </div>
            ) : (
              <div className="facturas-list">
                {/* Se muestran de la más reciente a la más antigua (el balance es acumulado por fecha) */}
                {[...estadoCuenta.transactions].reverse().map((transaction, index) => {
                  const isPago = transaction.type === 'payment';
                  const monto = isPago ? (transaction.credit || 0) : (transaction.debit || 0);
                  return (
                    <div key={index} className="factura-card">
                      <div className="factura-header">
                        <div className="factura-numero">{transaction.reference}</div>
                        <span
                          className="badge"
                          style={{
                            padding: '2px 10px',
                            borderRadius: '10px',
                            fontSize: '0.75em',
                            fontWeight: 600,
                            backgroundColor: isPago ? '#d1fae5' : '#e0e7ff',
                            color: isPago ? '#065f46' : '#3730a3'
                          }}
                        >
                          {isPago ? 'Pago' : 'Factura'}
                        </span>
                      </div>

                      <div className="factura-body">
                        <div className="factura-fechas">
                          <div className="fecha-item">
                            <span className="label">Fecha:</span>
                            <span>{formatFecha(transaction.date)}</span>
                          </div>
                        </div>

                        {transaction.description && (
                          <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#6b7280' }}>
                            {transaction.description}
                          </div>
                        )}
                      </div>

                      <div className="factura-footer">
                        <div className="factura-total">
                          <span className="label">{isPago ? 'Abono:' : 'Cargo:'}</span>
                          <span className="monto" style={{ color: isPago ? '#10b981' : '#ef4444' }}>
                            {isPago ? '-' : '+'}${monto.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                          </span>
                        </div>
                        {transaction.balance !== undefined && (
                          <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
                            Saldo: ${transaction.balance?.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal de envío por correo */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        title="Enviar Estado de Cuenta por Correo"
        size="medium"
      >
        <div className="modal-body">
          <div className="payment-info-summary">
            <p><strong>Cliente:</strong> {estadoCuenta?.client_name}</p>
            <p><strong>Período:</strong> {formatFecha(dateFrom)} - {formatFecha(dateTo)}</p>
            <p><strong>Adjunto:</strong> PDF del estado de cuenta (se genera automáticamente)</p>
          </div>

          <div className="form-group">
            <label className="form-label">Destinatario</label>
            <input
              type="email"
              className="form-input"
              value={emailData.destinatario}
              onChange={(e) => setEmailData(prev => ({ ...prev, destinatario: e.target.value }))}
              placeholder="Correo registrado del cliente (por defecto)"
            />
            <small className="form-helper">
              Déjalo vacío para enviar al correo registrado del cliente.
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Copias (CC)</label>
            <input
              type="text"
              className="form-input"
              value={emailData.copias}
              onChange={(e) => setEmailData(prev => ({ ...prev, copias: e.target.value }))}
              placeholder="correo1@dominio.com, correo2@dominio.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Asunto</label>
            <input
              type="text"
              className="form-input"
              value={emailData.asunto}
              onChange={(e) => setEmailData(prev => ({ ...prev, asunto: e.target.value }))}
              maxLength={200}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Mensaje</label>
            <textarea
              className="form-input"
              value={emailData.mensaje}
              onChange={(e) => setEmailData(prev => ({ ...prev, mensaje: e.target.value }))}
              placeholder="Déjalo vacío para usar el mensaje estándar (período y saldo actual)."
              rows={4}
              maxLength={5000}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn--outline"
            onClick={() => setShowEmailModal(false)}
            disabled={isSendingEmail}
          >
            <FiX size={18} />
            <span>Cancelar</span>
          </button>
          <button
            className="btn btn--primary"
            onClick={processSendEmail}
            disabled={isSendingEmail}
          >
            <FiMail size={18} />
            <span>{isSendingEmail ? 'Enviando...' : 'Enviar Correo'}</span>
          </button>
        </div>
      </Modal>

      {/* Menú inferior */}
      <BottomTabMenu baseRoute={`/system/clientes/${id}`} currentTab="estado-cuenta" />
    </div>
  );
};

export default ClienteEstadoCuenta;
